import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, FileText, Plus, Heart, Settings, QrCode } from 'lucide-react';
import QrScannerModal from './QrScannerModal';
import { getAuthUser } from '../repositories/LocalDatabase';

const CAMERA_PERMISSION_KEY = 'cameraPermissionGranted';

const BottomNav: React.FC = () => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const [showCameraPermissionModal, setShowCameraPermissionModal] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => getAuthUser().isLoggedIn);
  const navigate = useNavigate();
  const location = useLocation();

  const isListActive = location.pathname.startsWith('/recipes') || location.pathname.startsWith('/flavors');

  useEffect(() => {
    setIsLoggedIn(getAuthUser().isLoggedIn);
    const handleAuthChange = () => setIsLoggedIn(getAuthUser().isLoggedIn);
    window.addEventListener('hf-auth-change', handleAuthChange);
    return () => window.removeEventListener('hf-auth-change', handleAuthChange);
  }, []);

  useEffect(() => {
    if (!showAddMenu) {
      document.querySelectorAll('.add-button-container .ripple-effect').forEach((el) => el.remove());
    }
    if (!showListMenu) {
      document.querySelectorAll('.list-button-container .ripple-effect').forEach((el) => el.remove());
    }
    if (!showAddMenu && !showListMenu) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.add-button-container')) {
        setShowAddMenu(false);
      }
      if (!target.closest('.list-button-container')) {
        setShowListMenu(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showAddMenu, showListMenu]);

  const handleQrScanClick = () => {
    setShowAddMenu(false);
    const alreadyGranted = localStorage.getItem(CAMERA_PERMISSION_KEY) === 'true';
    if (alreadyGranted) {
      setShowQrScanner(true);
    } else {
      setShowCameraPermissionModal(true);
    }
  };

  const handlePermissionAllow = () => {
    localStorage.setItem(CAMERA_PERMISSION_KEY, 'true');
    setShowCameraPermissionModal(false);
    setShowQrScanner(true);
  };

  const handlePermissionDeny = () => {
    setShowCameraPermissionModal(false);
  };

  /**
   * QRスキャン結果を解析して適切な登録画面へ遷移する。
   * - ミックスカード（mixes/r/ URL or flavors[] を含む JSON）→ CreateRecipe
   * - フレーバーカード（flavors/f/ URL or フレーバー属性 JSON）→ FlavorList + AddFlavorModal
   * いずれも生テキストを scannedText として state 経由で渡す。
   */
  const handleQrScan = (decodedText: string) => {
    setShowQrScanner(false);

    // ミックスカード判定（コンパクト形式: { n, f[] } または旧形式: { name, flavors[] } または URL形式）
    const isMixUrl = /(?:mixes|r)\/([a-zA-Z0-9_-]+)/.test(decodedText);
    let isMixJson = false;
    try {
      const data = JSON.parse(decodedText);
      const mixFlavors = data.f || data.flavors;
      if (data && Array.isArray(mixFlavors) && mixFlavors.length > 0) isMixJson = true;
    } catch { /* ignore */ }

    if (isMixUrl || isMixJson) {
      // レシピ登録画面へ遷移（scannedText を state で渡す）
      navigate('/create-recipe', { state: { scannedText: decodedText } });
      return;
    }

    // フレーバーカード判定（URL形式 or JSON形式）
    const isFlavorUrl = /(?:flavors|f)\/([a-zA-Z0-9_-]+)/.test(decodedText);
    let isFlavorJson = false;
    try {
      const data = JSON.parse(decodedText);
      // flavors配列を持つものはミックスなので除外
      if (data && (data.name || data.flavorName || data.brand_name) && !Array.isArray(data.flavors)) {
        isFlavorJson = true;
      }
    } catch { /* ignore */ }

    if (isFlavorUrl || isFlavorJson) {
      // フレーバー一覧へ遷移しモーダルを開く（scannedText を state で渡す）
      navigate('/flavors', { state: { openAddModal: true, scannedText: decodedText } });
      window.dispatchEvent(
        new CustomEvent('open-add-flavor-modal', { detail: { scannedText: decodedText } })
      );
      return;
    }

    // 判定不能な場合はフレーバー登録モーダルを開く（空データ）
    navigate('/flavors', { state: { openAddModal: true } });
    window.dispatchEvent(new CustomEvent('open-add-flavor-modal'));
  };

  const navStyle: React.CSSProperties = isLoggedIn
    ? {
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 40px)',
        maxWidth: '600px',
        height: '70px',
        backgroundColor: 'var(--color-surface)',
        borderRadius: '100px',
        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.6), 0 1px 3px rgba(0,0,0,0.4)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        zIndex: 1000,
      }
    : {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '70px',
        backgroundColor: 'var(--color-surface)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
      };

  const navContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '0 20px',
    height: '100%',
    boxSizing: 'border-box',
  };

  const iconContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text)',
    textDecoration: 'none',
    opacity: 0.7,
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    zIndex: 2,
  };

  const activeStyle: React.CSSProperties = {
    ...iconContainerStyle,
    opacity: 1,
    color: 'var(--color-primary)',
  };

  const addButtonStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    backgroundColor: 'var(--color-primary)',
    borderRadius: '50%',
    color: 'var(--color-text)',
    boxShadow: '0 4px 10px rgba(107, 78, 255, 0.4)',
    cursor: 'pointer',
    transform: 'translateY(-15px)',
    transition: 'transform 0.2s, background-color 0.2s',
  };

  const addMenuPopupStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '70px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    padding: '10px',
    display: showAddMenu ? 'flex' : 'none',
    flexDirection: 'column',
    gap: '10px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'nowrap',
    zIndex: 1001,
  };

  const menuItemStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: 'var(--color-background)',
    borderRadius: '8px',
    color: 'var(--color-text)',
    cursor: 'pointer',
    textAlign: 'center',
    border: '1px solid transparent',
    transition: 'border-color 0.2s',
  };

  // カメラ権限確認モーダルのスタイル
  const permissionOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9998,
    padding: '20px',
  };

  const permissionModalStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '16px',
    padding: '32px 28px',
    maxWidth: '360px',
    width: '100%',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
  };

  return (
    <>
      <nav style={navStyle}>
        <div style={{
          position: 'absolute',
          bottom: '4px',
          right: '8px',
          fontSize: '10px',
          color: 'var(--color-text)',
          opacity: 0.3,
          pointerEvents: 'none',
          fontFamily: 'var(--font-numbers)'
        }}>
          v0.9.0
        </div>
        <div style={navContainerStyle}>
          <NavLink
            to="/"
            className="nav-item-btn"
            style={({ isActive }) => (isActive ? activeStyle : iconContainerStyle)}
            onClick={() => {
              setShowAddMenu(false);
              setShowListMenu(false);
            }}
          >
            {({ isActive }) => (
              <Home size={24} />
            )}
          </NavLink>

          {/* List Button with Popup Menu */}
          <div
            className="list-button-container"
            style={{ position: 'relative' }}
          >
            <div
              className="nav-item-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowListMenu(!showListMenu);
                setShowAddMenu(false);
              }}
              style={{
                ...iconContainerStyle,
                ...(showListMenu || isListActive ? activeStyle : {}),
                cursor: 'pointer',
              }}
            >
              <FileText size={24} />
            </div>

            {/* List Menu Popup */}
            <div
              style={{
                ...addMenuPopupStyle,
                display: showListMenu ? 'flex' : 'none',
              }}
            >
              <div
                className="ripple-card"
                style={menuItemStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => {
                  setTimeout(() => {
                    navigate('/recipes');
                    setShowListMenu(false);
                  }, 200);
                }}
              >
                レシピ一覧
              </div>
              <div
                className="ripple-card"
                style={menuItemStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => {
                  setTimeout(() => {
                    navigate('/flavors');
                    setShowListMenu(false);
                  }, 200);
                }}
              >
                フレーバー一覧
              </div>
            </div>
          </div>

          {/* Add Button with Hover/Click Menu */}
          <div
            className="add-button-container"
            style={{ position: 'relative' }}
          >
            <div
              className="ripple-card"
              style={addButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                setShowAddMenu(!showAddMenu);
                setShowListMenu(false);
              }}
            >
              <Plus size={32} />
            </div>

            <div style={addMenuPopupStyle}>
              <div
                className="ripple-card"
                style={menuItemStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => {
                  setTimeout(() => {
                    navigate('/flavors', { state: { openAddModal: true } });
                    window.dispatchEvent(new CustomEvent('open-add-flavor-modal'));
                    setShowAddMenu(false);
                  }, 200);
                }}
              >
                フレーバーを登録
              </div>
              <div
                className="ripple-card"
                style={menuItemStyle}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => {
                  setTimeout(() => {
                    navigate('/create-recipe');
                    setShowAddMenu(false);
                  }, 200);
                }}
              >
                レシピを登録
              </div>
              <div
                className="ripple-card"
                style={{ ...menuItemStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => {
                  setTimeout(() => {
                    handleQrScanClick();
                  }, 200);
                }}
              >
                <QrCode size={16} />
                QRをスキャン
              </div>
            </div>
          </div>

          <NavLink
            to="/favorites"
            className="nav-item-btn"
            style={({ isActive }) => (isActive ? activeStyle : iconContainerStyle)}
            onClick={() => {
              setShowAddMenu(false);
              setShowListMenu(false);
            }}
          >
            {({ isActive }) => (
              <Heart size={24} />
            )}
          </NavLink>

          <NavLink
            to="/settings"
            className="nav-item-btn"
            style={({ isActive }) => (isActive ? activeStyle : iconContainerStyle)}
            onClick={() => {
              setShowAddMenu(false);
              setShowListMenu(false);
            }}
          >
            {({ isActive }) => (
              <Settings size={24} />
            )}
          </NavLink>
        </div>
      </nav>

      {/* カメラ権限確認モーダル（初回のみ表示） */}
      {showCameraPermissionModal && (
        <div style={permissionOverlayStyle}>
          <div style={permissionModalStyle}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(107, 78, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '8px',
            }}>
              <QrCode size={32} color="var(--color-primary)" />
            </div>
            <h3 style={{ color: 'var(--color-text)', margin: 0, fontSize: '18px' }}>
              カメラへのアクセス
            </h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
              QRコードをスキャンするためにカメラへのアクセスが必要です。フレーバーカードやミックスカードの情報を自動入力できます。
            </p>
            <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
              <button
                className="ripple-card"
                onClick={handlePermissionDeny}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-surface-hover)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                }}
              >
                許可しない
              </button>
              <button
                className="ripple-card"
                onClick={handlePermissionAllow}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  boxShadow: '0 4px 10px rgba(107, 78, 255, 0.4)',
                }}
              >
                許可する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QRスキャナーモーダル */}
      {showQrScanner && (
        <QrScannerModal
          onScan={handleQrScan}
          onClose={() => setShowQrScanner(false)}
        />
      )}
    </>
  );
};

export default BottomNav;
